import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import type { ServiceKey } from '@shared/api/config';
import { key } from '@shared/api/query-keys';

import { configurationsKey } from './queries';

/**
 * The two writes this surface owns — and **both go through the same route**,
 * because that is what the owning services declare.
 *
 * `PATCH /api/v1/admin/configurations/{key}` writes `value` and `description`.
 * The runtime grant on every one of those tables is exactly
 * `SELECT, UPDATE (value, description)`, so the API could not create, delete, or
 * rename a key even if a screen asked it to. **On a secret-classed key the same
 * write is the rotation**: the new value is stored, the old one is not returned,
 * and the response carries no value at all.
 *
 * The console keeps the two apart at the *surface* rather than at the route,
 * which is where the distinction actually matters: a secret is rotated in its
 * own dialog, from an uncontrolled field, with copy that says what stops working
 * — and the value never reaches component state on either path.
 *
 * **Nothing auto-retries.** A configuration write takes effect on the service at
 * its next read, the audit log that would record it is deferred, and the confirm
 * step is the only guard that ships — a client that retried one of these would
 * be guessing at an outcome the operator has to see.
 *
 * **The invalidation set reaches past this page.** A key that feeds a vocabulary
 * the console renders elsewhere — the case states, the credit roles, the shelf
 * sources — has to be re-read where it is *used*, not only where it was edited,
 * or the console keeps rendering yesterday's setting on a surface nobody
 * re-opened.
 */

/** The vocabularies a configuration write can move, keyed by the setting that feeds them. */
const VOCABULARY_DEPENDENTS: Readonly<Record<string, readonly (readonly [ServiceKey, string])[]>> = {
  CASE_STATES: [['support', 'caseStates']],
  CREDIT_ROLES: [['content', 'creditProfiles']],
  SHELF_SOURCES: [['content', 'shelves']],
  SHELF_LAYOUTS: [['content', 'shelves']],
  CATEGORIES: [['content', 'categories']],
  TAGS: [['content', 'tags']],
};

function useConfigurationInvalidation() {
  const queryClient = useQueryClient();

  return (service: ServiceKey, configurationKey: string) => {
    void queryClient.invalidateQueries({ queryKey: configurationsKey(service) });

    for (const [dependentService, resource] of VOCABULARY_DEPENDENTS[configurationKey] ?? []) {
      void queryClient.invalidateQueries({ queryKey: key(dependentService, resource) });
    }
  };
}

/**
 * **The nine services do not agree on how a configuration is written, and this
 * table is that disagreement written down rather than averaged away.**
 *
 * Seven declare `PATCH /admin/configurations/{key}`; `backend-notification` and
 * `backend-finance` declare `PUT` on the same path. A console that picked one
 * verb would be sending a method two of its nine sections do not route, and the
 * failure would arrive as a `405` an operator cannot act on.
 *
 * This is a contract defect on the owning side, not a console preference. The
 * fix is one MR against those two PRD-TDDs; until it lands the console follows
 * each service's own declaration, because the alternative is a page that is
 * quietly broken on two of its nine sections.
 */
const WRITE_METHOD: Readonly<Record<ServiceKey, 'patch' | 'put'>> = {
  auth: 'patch',
  content: 'patch',
  assets: 'patch',
  search: 'patch',
  engagement: 'patch',
  analytics: 'patch',
  notification: 'put',
  finance: 'put',
  support: 'patch',
};

/**
 * **And they do not agree on how a *secret* is written either.**
 *
 * `backend-auth` alone declares a dedicated `POST …/{key}:rotate`, and its §9 is
 * explicit that `PATCH` on a secret key is *refused* — so on that service the
 * rotate route is not a nicety, it is the only path that exists. Every other
 * service declares the opposite: the ordinary write **is** the rotation on a
 * secret-classed key, the new value is stored, and nothing of it comes back.
 *
 * The surface is identical either way — one dialog, one uncontrolled field, one
 * sentence about what stops working. Only the wire differs.
 */
const SERVICES_WITH_ROTATE_ROUTE: readonly ServiceKey[] = ['auth'];

export interface ConfigurationWriteBody {
  /** Omitted on a description-only save, and never sent for a secret key. */
  value?: string;
  description?: string;
}

function write(service: ServiceKey, path: string, body: unknown) {
  return WRITE_METHOD[service] === 'put'
    ? api.put<null>(service, path, body)
    : api.patch<null>(service, path, body);
}

export function useUpdateConfiguration() {
  const invalidate = useConfigurationInvalidation();

  return useMutation({
    mutationFn: ({
      service,
      configurationKey,
      body,
    }: {
      service: ServiceKey;
      configurationKey: string;
      body: ConfigurationWriteBody;
    }) => write(service, `/admin/configurations/${configurationKey}`, body),
    onSuccess: (_data, variables) => invalidate(variables.service, variables.configurationKey),
    retry: false,
  });
}

/**
 * Rotation — one surface, two wires, per the table above.
 *
 * The new value is read from an uncontrolled field at the moment of the call and
 * is never held in component state, written to a cell, or put on an attribute:
 * a secret left in an element's `value` is readable from the browser's own tools
 * after the dialog closed over it.
 */
export function useRotateConfiguration() {
  const invalidate = useConfigurationInvalidation();

  return useMutation({
    mutationFn: ({
      service,
      configurationKey,
      value,
    }: {
      service: ServiceKey;
      configurationKey: string;
      value: string;
    }) =>
      SERVICES_WITH_ROTATE_ROUTE.includes(service)
        ? api.post<null>(service, `/admin/configurations/${configurationKey}:rotate`, { value })
        : write(service, `/admin/configurations/${configurationKey}`, { value }),
    onSuccess: (_data, variables) => invalidate(variables.service, variables.configurationKey),
    retry: false,
  });
}
